import { MAP_STYLES } from "@/config/constants";
import { useCallback, useRef, type RefObject } from "react";
import type { FeatureCollection, LineString } from "geojson";
import { RouteResultForMapView } from "../types/routing";
import * as maplibregl from "maplibre-gl";

type Rgb = [number, number, number];

// 経路線が出発地から目的地へ伸びるアニメーションを繰り返す。未到達部分は薄い下地で見せる
const ROUTE_DRAW_MS = 1800;
const ROUTE_HOLD_MS = 1200;
const ROUTE_BASE_OPACITY = 0.35;
const ROUTE_FLOW_OPACITY = 0.9;
// line-gradient の再設定は重いので小さな変化では行わない
const ROUTE_FLOW_MIN_STEP = 0.005;
const ROUTE_FLOW_EDGE = 0.002;

const parseHexColor = (hex: string): Rgb => {
  const h = hex.replace("#", "");
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as Rgb;
};

const ROUTE_START_RGB = parseHexColor(MAP_STYLES.routeStartColor);
const ROUTE_GOAL_RGB = parseHexColor(MAP_STYLES.routeGoalColor);

// 下地グラデーション上の色 (RGB 線形補間)
const routeColorAt = (progress: number, alpha = 1): string => {
  const [r, g, b] = ROUTE_START_RGB.map((c, i) =>
    Math.round(c + (ROUTE_GOAL_RGB[i] - c) * progress),
  );
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const buildRouteGradient = (): maplibregl.ExpressionSpecification => [
  "interpolate",
  ["linear"],
  ["line-progress"],
  0,
  MAP_STYLES.routeStartColor,
  1,
  MAP_STYLES.routeGoalColor,
];

// head (0..1) まで色を付け、その先を透明にしたグラデーション
const buildFlowGradient = (head: number): maplibregl.ExpressionSpecification => {
  if (head >= 1) return buildRouteGradient();
  const transparent = routeColorAt(head, 0);
  if (head <= 0) {
    return ["interpolate", ["linear"], ["line-progress"], 0, transparent, 1, transparent];
  }
  const edge = Math.min(head + ROUTE_FLOW_EDGE, 1);
  return [
    "interpolate",
    ["linear"],
    ["line-progress"],
    0,
    MAP_STYLES.routeStartColor,
    head,
    routeColorAt(head),
    edge,
    transparent,
    ...(edge < 1 ? [1, transparent] : []),
  ] as maplibregl.ExpressionSpecification;
};

const easeInOut = (x: number) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);

export const useRouteDisplay = (map: RefObject<maplibregl.Map | null>) => {
  const routeSourceId = "route-source";
  const routeLayerId = "route-layer";
  const routeFlowLayerId = "route-flow-layer";
  const stopAnimationRef = useRef<(() => void) | null>(null);
  const latestRouteRef = useRef<RouteResultForMapView | null>(null);

  // ルートレイヤーを最前面に移動
  const bringRouteLayersToFront = useCallback(() => {
    if (!map.current) return;
    [routeLayerId, routeFlowLayerId].forEach((layerId) => {
      if (map.current?.getLayer(layerId)) {
        map.current.moveLayer(layerId);
      }
    });
  }, [map]);

  const startAnimationForFloor = (floorId: string) => {
    if (!map.current) return;

    // 停止
    if (stopAnimationRef.current) {
      stopAnimationRef.current();
      stopAnimationRef.current = null;
    }

    const newRoute = latestRouteRef.current;
    if (!newRoute) return;

    const hasLineOnFloor = (
      newRoute.lineGeometry as FeatureCollection<LineString, { floorId?: string }>
    ).features.some((f) => f.properties?.floorId === floorId && f.geometry?.type === "LineString");
    if (!hasLineOnFloor) return;

    let lastHead = -1;
    stopAnimationRef.current = startTick(ROUTE_DRAW_MS + ROUTE_HOLD_MS, (elapsedInCycle) => {
      const mapInstance = map.current;
      if (!mapInstance || !mapInstance.getLayer(routeFlowLayerId)) return;
      const head = elapsedInCycle >= ROUTE_DRAW_MS ? 1 : easeInOut(elapsedInCycle / ROUTE_DRAW_MS);
      const isCycleRestart = head < lastHead;
      if (!isCycleRestart && Math.abs(head - lastHead) < ROUTE_FLOW_MIN_STEP && head < 1) return;
      if (head === lastHead) return;
      lastHead = head;
      mapInstance.setPaintProperty(routeFlowLayerId, "line-gradient", buildFlowGradient(head));
    });
  };

  // durationMs 周期で、周期内の経過時間 (ms) を onTick に渡す
  const startTick = (durationMs: number, onTick: (elapsedInCycle: number) => void) => {
    const start = performance.now();
    let rafId: number;
    const loop = (now: number) => {
      onTick((now - start) % durationMs);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
    };
  };

  const displayRoute = (floorId: string, newRoute: RouteResultForMapView | null): void => {
    if (!map.current || !newRoute) return;

    clearRouteDisplay();

    map.current.addSource(routeSourceId, {
      type: "geojson",
      data: newRoute.lineGeometry,
      lineMetrics: true,
    });
    // 下地: 経路全体を薄く表示
    map.current.addLayer({
      id: routeLayerId,
      type: "line",
      source: routeSourceId,
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
      paint: {
        "line-gradient": buildRouteGradient(),
        "line-width": MAP_STYLES.routeWidth,
        "line-opacity": ROUTE_BASE_OPACITY,
      },
    });
    // 伸びていく線
    map.current.addLayer({
      id: routeFlowLayerId,
      type: "line",
      source: routeSourceId,
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
      paint: {
        "line-gradient": buildFlowGradient(0),
        "line-width": MAP_STYLES.routeWidth,
        "line-opacity": ROUTE_FLOW_OPACITY,
      },
    });
    latestRouteRef.current = newRoute;

    bringRouteLayersToFront();
    updateRouteLayerForFloor(floorId);
  };

  const clearRouteDisplay = () => {
    if (!map.current) return;

    // アニメーションがあれば停止
    if (stopAnimationRef.current) {
      stopAnimationRef.current();
      stopAnimationRef.current = null;
    }

    [routeFlowLayerId, routeLayerId].forEach((layerId) => {
      if (map.current?.getLayer(layerId)) {
        map.current.removeLayer(layerId);
      }
    });
    if (map.current.getSource(routeSourceId)) {
      map.current.removeSource(routeSourceId);
    }
  };

  const updateRouteLayerForFloor = (floorId: string) => {
    if (!map.current || !map.current.getLayer(routeLayerId)) return;

    const floorFilter: maplibregl.FilterSpecification = ["==", ["get", "floorId"], floorId];
    map.current.setFilter(routeLayerId, floorFilter);
    if (map.current.getLayer(routeFlowLayerId)) {
      map.current.setFilter(routeFlowLayerId, floorFilter);
    }

    startAnimationForFloor(floorId);
  };

  return {
    displayRoute,
    clearRouteDisplay,
    updateRouteLayerForFloor,
    bringRouteLayersToFront,
  };
};
